import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { RefreshCw, Trash2, X } from 'lucide-react';
import { ERP_PROVIDERS } from '@/types/onboarding';
import { Card, CardTitle } from '@components/common/Card';
import { Button } from '@components/common/Button';
import {
  useDismissErpRealmSwitch,
  useExecuteErpRealmSwitch,
  useGetAjeraCredentials,
  useGetErpConnection,
  useGetErpConnections,
  useErpConnectionRemovalPreview,
  useRemoveErpConnection,
  usePendingErpRealmSwitch,
  useSyncErpConnection,
  useUpdateErpConnection,
} from '@api/hooks/useErpConnection';
import {
  type ErpConnectionCredentials,
  getErpProviderLabel,
  isErpAuthProvider,
  requestErpAuthorization,
  type ErpAuthProvider,
} from '@api/quickbooksAuth';
import { useErpAuth } from '@api/hooks/useQuickBooksAuth';
import { useNotificationStore } from '@store/notificationStore';
import { ErpRealmSwitchModal } from './ErpRealmSwitchModal';
import { QuickBooksDesktopConnectorSettings } from './QuickBooksDesktopConnectorSettings';
import type { ERPConnectionRemovalRequest, ErpRealmSwitchOptions } from '@schemas/api';

const resolveErpAuthProvider = (erpType?: string | null): ErpAuthProvider | null =>
  isErpAuthProvider(erpType) ? erpType : null;

export const ErpIntegrationSettings = () => {
  const { data: connection } = useGetErpConnection();
  const { data: connections = [] } = useGetErpConnections();
  const queryClient = useQueryClient();
  const [selectedProvider, setSelectedProvider] = useState<ErpAuthProvider | null>(
    resolveErpAuthProvider(connection?.erp_type)
  );
  const [ajeraForm, setAjeraForm] = useState<ErpConnectionCredentials>({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRealmSwitchModalOpen, setIsRealmSwitchModalOpen] = useState(false);
  const { addNotification } = useNotificationStore();
  const { data: pendingRealmSwitch } = usePendingErpRealmSwitch();
  const { data: ajeraCredentials } = useGetAjeraCredentials(selectedProvider === 'ajera');
  const executeRealmSwitch = useExecuteErpRealmSwitch();
  const dismissRealmSwitch = useDismissErpRealmSwitch();
  const [removingConnectionId, setRemovingConnectionId] = useState<string>();
  const [removalOptions, setRemovalOptions] = useState<ERPConnectionRemovalRequest>({
    remove_vendor_links: false,
    archive_exclusive_vendors: false,
    remove_documents: false,
    remove_ap_postings: false,
    remove_catalogs: false,
  });
  const removalPreview = useErpConnectionRemovalPreview(removingConnectionId);
  const removeConnection = useRemoveErpConnection();
  const syncConnection = useSyncErpConnection();
  const updateConnection = useUpdateErpConnection();
  const [editingConnectionId, setEditingConnectionId] = useState<string>();
  const [editingValue, setEditingValue] = useState('');
  const editCommittedRef = useRef(false);

  const closeRemoval = () => {
    setRemovingConnectionId(undefined);
    setRemovalOptions({
      remove_vendor_links: false,
      archive_exclusive_vendors: false,
      remove_documents: false,
      remove_ap_postings: false,
      remove_catalogs: false,
    });
  };

  const startEditingName = (connectionId: string, currentValue: string) => {
    editCommittedRef.current = false;
    setEditingConnectionId(connectionId);
    setEditingValue(currentValue);
  };

  const cancelEditingName = () => {
    editCommittedRef.current = true;
    setEditingConnectionId(undefined);
  };

  const commitEditingName = (connectionId: string, currentValue: string) => {
    if (editCommittedRef.current) {
      return;
    }
    editCommittedRef.current = true;
    setEditingConnectionId(undefined);

    const trimmed = editingValue.trim();
    if (trimmed === currentValue) {
      return;
    }
    updateConnection.mutate({
      connectionId,
      payload: { display_name: trimmed === '' ? null : trimmed },
    });
  };

  useEffect(() => {
    if (isErpAuthProvider(connection?.erp_type)) {
      setSelectedProvider(connection.erp_type);
    }
  }, [connection?.erp_type]);

  useEffect(() => {
    if (!ajeraCredentials) {
      return;
    }
    setAjeraForm((prev) => ({
      ...prev,
      api_url: ajeraCredentials.api_url ?? '',
      api_id: ajeraCredentials.api_id ?? '',
      username: ajeraCredentials.username ?? '',
      password: '',
    }));
  }, [ajeraCredentials]);

  useEffect(() => {
    if (!pendingRealmSwitch) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('erp_realm_switch_pending') === 'true') {
      params.delete('erp_realm_switch_pending');
      const nextSearch = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`
      );
    }
    setIsRealmSwitchModalOpen(true);
  }, [pendingRealmSwitch]);

  const connectedProvider = resolveErpAuthProvider(connection?.erp_type);
  const { authState } = useErpAuth({
    enabled: Boolean(connection?.realm_id && connectedProvider),
    provider: connectedProvider ?? selectedProvider ?? 'quickbooks',
  });
  const connectionIsChecking = Boolean(connectedProvider && authState.isChecking);
  const connectionIsAuthenticated = connectedProvider
    ? authState.isAuthenticated
    : connection?.status === 'active';
  const connectedProviderLabel = getErpProviderLabel(connection?.erp_type);

  const handleConnect = async () => {
    if (!selectedProvider) {
      addNotification({
        type: 'error',
        message: 'Choose an ERP provider to connect.',
      });
      return;
    }

    if (selectedProvider === 'ajera') {
      const missingApiId = !(ajeraForm.api_url || '').includes('?') && !ajeraForm.api_id;
      const missingPassword = !ajeraForm.password && !ajeraCredentials?.has_password;
      if (!ajeraForm.api_url || !ajeraForm.username || missingApiId || missingPassword) {
        addNotification({
          type: 'error',
          message: 'Enter the Ajera API URL, API ID, username, and password.',
        });
        return;
      }
    }

    setIsConnecting(true);
    try {
      const { authorization_url, is_authenticated } = await requestErpAuthorization(selectedProvider, {
        forceReconnect: selectedProvider !== 'ajera',
        returnPath: '/admin/settings?tab=integrations',
        credentials: selectedProvider === 'ajera' ? ajeraForm : undefined,
      });

      if (authorization_url) {
        window.location.href = authorization_url;
        return;
      }

      if (is_authenticated) {
        void queryClient.invalidateQueries({ queryKey: ['erp-connection'] });
        void queryClient.invalidateQueries({ queryKey: ['erp-connections'] });
        void queryClient.invalidateQueries({ queryKey: ['erp-credentials', 'ajera'] });
        addNotification({
          type: 'success',
          message: `${getErpProviderLabel(selectedProvider)} connected.`,
        });
        return;
      }

      addNotification({
        type: 'error',
        message: `${getErpProviderLabel(selectedProvider)} connection is unavailable. Please contact support.`,
      });
    } catch {
      addNotification({
        type: 'error',
        message: `Failed to initiate ${getErpProviderLabel(selectedProvider)} connection.`,
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleExecuteRealmSwitch = (options: ErpRealmSwitchOptions) => {
    if (!pendingRealmSwitch) {
      return;
    }
    executeRealmSwitch.mutate(
      { realmSwitchId: pendingRealmSwitch.id, options },
      { onSuccess: () => setIsRealmSwitchModalOpen(false) }
    );
  };

  const handleDismissRealmSwitch = () => {
    if (!pendingRealmSwitch) {
      return;
    }
    dismissRealmSwitch.mutate(pendingRealmSwitch.id, {
      onSuccess: () => setIsRealmSwitchModalOpen(false),
    });
  };

  return (
    <>
      <Card>
        <div className="p-6 space-y-4">
          <CardTitle>ERP Integration</CardTitle>
          <p className="text-sm text-content-secondary">
            Connections for the active tenant.
          </p>

          {connections.length > 0 && (
            <div className="overflow-x-auto border-y border-gray-200 dark:border-gray-700">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-content-secondary">
                  <tr>
                    <th className="px-2 py-2">Company</th>
                    <th className="px-2 py-2">Provider</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="w-12 px-2 py-2"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {connections.map((item) => {
                    const connectionName = item.display_name || item.external_company_name || item.realm_id;
                    const syncPending = syncConnection.isPending && syncConnection.variables === item.id;
                    const canSync = item.status === 'active';

                    return (
                      <tr key={item.id} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="px-2 py-2">
                          {editingConnectionId === item.id ? (
                            <input
                              type="text"
                              autoFocus
                              value={editingValue}
                              maxLength={255}
                              aria-label={`Connection name for ${connectionName}`}
                              onChange={(event) => setEditingValue(event.target.value)}
                              onFocus={(event) => event.currentTarget.select()}
                              onBlur={() => commitEditingName(item.id, connectionName)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  commitEditingName(item.id, connectionName);
                                } else if (event.key === 'Escape') {
                                  event.preventDefault();
                                  cancelEditingName();
                                }
                              }}
                              className="w-full rounded border border-primary-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-700 dark:bg-gray-800"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => startEditingName(item.id, connectionName)}
                              className="w-full text-left font-medium text-content-primary hover:underline"
                              title="Rename connection"
                              aria-label={`Rename ${connectionName}`}
                            >
                              {connectionName}
                            </button>
                          )}
                        </td>
                        <td className="px-2 py-2">{getErpProviderLabel(item.erp_type)}</td>
                        <td className="px-2 py-2 capitalize">{item.status.replace(/_/g, ' ')}</td>
                        <td className="px-2 py-2 text-right">
                          <div className="inline-flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => syncConnection.mutate(item.id)}
                              disabled={!canSync || syncConnection.isPending}
                              className="rounded p-2 text-content-muted hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-primary-950/40"
                              title={canSync ? 'Sync ERP connection' : 'Only active ERP connections can sync'}
                              aria-label={`Sync ${connectionName}`}
                            >
                              <RefreshCw className={clsx('h-4 w-4', syncPending && 'animate-spin')} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setRemovingConnectionId(item.id)}
                              className="rounded p-2 text-content-muted hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40"
                              title="Remove ERP connection"
                              aria-label={`Remove ${connectionName}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        <div className="grid grid-cols-2 gap-3">
          {ERP_PROVIDERS.filter((provider) => provider.available).map((provider) => (
            <button
              key={provider.key}
              type="button"
              onClick={() => setSelectedProvider(provider.key as ErpAuthProvider)}
              className={clsx(
                'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                selectedProvider === provider.key
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
              )}
            >
              {provider.label}
            </button>
          ))}
        </div>

        {selectedProvider === 'ajera' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              type="url"
              value={ajeraForm.api_url ?? ''}
              onChange={(event) => setAjeraForm((prev) => ({ ...prev, api_url: event.target.value }))}
              placeholder="Ajera API URL"
              className="input"
              autoComplete="off"
            />
            <input
              type="text"
              value={ajeraForm.api_id ?? ''}
              onChange={(event) => setAjeraForm((prev) => ({ ...prev, api_id: event.target.value }))}
              placeholder="API ID"
              className="input"
              autoComplete="off"
            />
            <input
              type="text"
              value={ajeraForm.username ?? ''}
              onChange={(event) => setAjeraForm((prev) => ({ ...prev, username: event.target.value }))}
              placeholder="Username"
              className="input"
              autoComplete="username"
            />
            <input
              type="password"
              value={ajeraForm.password ?? ''}
              onChange={(event) => setAjeraForm((prev) => ({ ...prev, password: event.target.value }))}
              placeholder={ajeraCredentials?.has_password ? 'Password unchanged' : 'Password'}
              className="input"
              autoComplete="current-password"
            />
          </div>
        )}

        {/* Connection status */}
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
            connectionIsChecking
              ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
              : connectionIsAuthenticated
              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
          }`}
        >
          <span
            className={`inline-block h-2 w-2 rounded-full ${
              connectionIsChecking
                ? 'bg-yellow-500 animate-pulse'
                : connectionIsAuthenticated
                ? 'bg-green-500'
                : 'bg-gray-400'
            }`}
          />
          {connectionIsChecking
            ? `Checking ${connectedProviderLabel} connection…`
            : connectionIsAuthenticated
            ? `Connected to ${connectedProviderLabel}${connection?.realm_id ? ` — Realm: ${connection.realm_id}` : ''}`
            : `Not connected${selectedProvider ? ` to ${getErpProviderLabel(selectedProvider)}` : ''}`}
        </div>

          <Button
            variant="primary"
            onClick={handleConnect}
            disabled={isConnecting || connectionIsChecking || !selectedProvider}
          >
            {isConnecting
              ? 'Connecting…'
              : connectionIsAuthenticated
              ? `Add ${getErpProviderLabel(selectedProvider)} company`
              : selectedProvider
                ? `Connect to ${getErpProviderLabel(selectedProvider)}`
                : 'Choose ERP provider'}
          </Button>
        </div>
      </Card>

      <QuickBooksDesktopConnectorSettings connections={connections} />

      {removingConnectionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-xl rounded-lg bg-white shadow-xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-content-primary dark:text-content-primary-dark">Remove ERP connection</h2>
                <p className="text-sm text-content-secondary">{removalPreview.data?.connection_name || 'Loading impact...'}</p>
              </div>
              <button type="button" onClick={closeRemoval} className="rounded p-2 text-content-muted hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              {(removalPreview.data?.warnings || []).map((warning) => (
                <p key={warning} className="text-sm text-amber-800 dark:text-amber-300">{warning}</p>
              ))}
              <div className="space-y-2">
                {([
                  ['remove_vendor_links', 'Remove ERP vendor links', 'vendor_links'],
                  ['archive_exclusive_vendors', 'Archive vendors only linked to this ERP', 'exclusive_vendors'],
                  ['remove_documents', 'Remove linked invoices, purchase orders, and receipts', null],
                  ['remove_ap_postings', 'Remove linked AP postings', 'ap_postings'],
                  ['remove_catalogs', 'Remove synced items, accounts, projects, and ERP fields', null],
                ] as const).map(([key, label, countKey]) => {
                  const count = countKey
                    ? removalPreview.data?.counts[countKey]
                    : key === 'remove_documents'
                      ? (removalPreview.data?.counts.invoices || 0) + (removalPreview.data?.counts.purchase_orders || 0) + (removalPreview.data?.counts.receipts || 0)
                      : (removalPreview.data?.counts.items || 0) + (removalPreview.data?.counts.accounts || 0) + (removalPreview.data?.counts.projects_contacts || 0) + (removalPreview.data?.counts.erp_fields || 0);
                  return (
                    <label key={key} className="flex items-start gap-3 rounded border border-gray-200 px-3 py-3 text-sm dark:border-gray-700">
                      <input
                        type="checkbox"
                        checked={removalOptions[key]}
                        disabled={key === 'remove_ap_postings' && removalOptions.remove_documents}
                        onChange={(event) => setRemovalOptions((current) => ({
                          ...current,
                          [key]: event.target.checked,
                          ...(key === 'remove_documents' && event.target.checked ? { remove_ap_postings: true } : {}),
                        }))}
                        className="mt-0.5 h-4 w-4"
                      />
                      <span className="flex-1 text-content-primary dark:text-content-primary-dark">{label}</span>
                      <span className="text-content-muted">{count ?? 0}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              <Button variant="secondary" onClick={closeRemoval} disabled={removeConnection.isPending}>Cancel</Button>
              <Button
                variant="danger"
                disabled={removalPreview.isLoading || removeConnection.isPending}
                onClick={() => removeConnection.mutate(
                  { connectionId: removingConnectionId, options: removalOptions },
                  { onSuccess: closeRemoval },
                )}
              >
                {removeConnection.isPending ? 'Removing...' : 'Remove connection'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ErpRealmSwitchModal
        open={isRealmSwitchModalOpen}
        realmSwitch={pendingRealmSwitch ?? null}
        isExecuting={executeRealmSwitch.isPending}
        isDismissing={dismissRealmSwitch.isPending}
        onOpenChange={setIsRealmSwitchModalOpen}
        onExecute={handleExecuteRealmSwitch}
        onDismiss={handleDismissRealmSwitch}
      />
    </>
  );
};
